import jsPDF from 'jspdf';
// Import jspdf-autotable - this extends jsPDF with autoTable method
// Using side-effect import to ensure the plugin is loaded
import 'jspdf-autotable';

// Fonction pour s'assurer que autoTable est disponible
// Charge dynamiquement le plugin si nécessaire
async function ensureAutoTable(doc) {
  if (typeof doc.autoTable === 'function') {
    return true;
  }
  
  // Si autoTable n'est pas disponible, essayer de charger le plugin dynamiquement
  try {
    await import('jspdf-autotable');
    // Le plugin devrait maintenant être disponible
    return typeof doc.autoTable === 'function';
  } catch (error) {
    console.warn('Could not load jspdf-autotable:', error);
    return false;
  }
}

// Informations de l'entreprise (à personnaliser selon vos besoins)
const COMPANY_INFO = {
  name: 'Call Garden',
  address: 'Votre adresse',
  city: 'Votre ville',
  postalCode: 'Code postal',
  phone: 'Téléphone',
  email: 'contact@callgarden.fr',
  siret: 'SIRET',
  tva: 'TVA'
};

// Fonction pour mettre à jour les informations de l'entreprise
export function setCompanyInfo(info) {
  Object.assign(COMPANY_INFO, info);
}

// Fonction utilitaire pour nettoyer les noms de fichiers
function sanitizeFileName(name) {
  if (!name) return '';
  return String(name)
    .replace(/[^a-z0-9]/gi, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

// Fonction utilitaire pour formater une date en toute sécurité
function formatDate(date) {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('fr-FR');
  } catch (error) {
    return 'N/A';
  }
}

/**
 * Génère un PDF pour un devis
 */
export async function generateQuotePDF(quote, client, prospect) {
  try {
    if (!quote) {
      throw new Error('Les données du devis sont manquantes');
    }
    
    const doc = new jsPDF();
    
    // S'assurer que autoTable est disponible
    await ensureAutoTable(doc);
    
    // Couleurs
    const primaryColor = [22, 163, 74]; // Vert (primary-600)
    const grayColor = [107, 114, 128];
    
    // En-tête
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('DEVIS', 20, 25);
    
    // Informations entreprise
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(COMPANY_INFO.name, 20, 35);
    
    // Informations du devis
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`N° Devis: ${quote.quote_number || 'N/A'}`, 150, 50);
    doc.text(`Date: ${formatDate(quote.created_at)}`, 150, 56);
    if (quote.valid_until) {
      doc.text(`Valide jusqu'au: ${formatDate(quote.valid_until)}`, 150, 62);
    }
    
    // Client/Prospect
    const recipient = client || prospect;
    const recipientName = client 
      ? (client.client_type === 'entreprise' ? client.company_name : `${client.first_name || ''} ${client.last_name}`.trim())
      : `${prospect?.first_name || ''} ${prospect?.last_name || ''}`.trim();
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Destinataire:', 20, 75);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(recipientName, 20, 82);
    
    if (recipient?.address) {
      doc.text(recipient.address, 20, 88);
    }
    if (recipient?.phone) {
      doc.text(`Tél: ${recipient.phone}`, 20, 94);
    }
    if (recipient?.email) {
      doc.text(`Email: ${recipient.email}`, 20, 100);
    }
    
    // Titre du devis
    let yPos = 110;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(quote.title || 'Devis', 20, yPos);
    yPos += 10;
    
    // Description
    if (quote.description) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const descriptionLines = doc.splitTextToSize(quote.description, 170);
      doc.text(descriptionLines, 20, yPos);
      yPos += descriptionLines.length * 5 + 5;
    }
    
    // Tableau des montants
    yPos += 5;
    
    // Vérifier que autoTable est disponible
    if (typeof doc.autoTable === 'function') {
      doc.autoTable({
        startY: yPos,
        head: [['Description', 'Montant HT', 'TVA', 'Montant TTC']],
        body: [[
          quote.title || 'Prestation',
          `${(quote.total_amount || 0).toFixed(2)} €`,
          `${quote.tax_rate || 20}%`,
          `${(quote.total_with_tax || 0).toFixed(2)} €`
        ]],
        theme: 'striped',
        headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10 },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { halign: 'right' },
          2: { halign: 'center' },
          3: { halign: 'right', fontStyle: 'bold' }
        }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    } else {
      // Fallback: créer un tableau simple sans autoTable
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Description', 20, yPos);
      doc.text('Montant HT', 100, yPos);
      doc.text('TVA', 140, yPos);
      doc.text('Montant TTC', 170, yPos);
      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.text(quote.title || 'Prestation', 20, yPos);
      doc.text(`${(quote.total_amount || 0).toFixed(2)} €`, 100, yPos);
      doc.text(`${quote.tax_rate || 20}%`, 140, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(`${(quote.total_with_tax || 0).toFixed(2)} €`, 170, yPos);
      yPos += 10;
    }
    
    // Total
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Total TTC:', 150, yPos);
    doc.text(`${(quote.total_with_tax || 0).toFixed(2)} €`, 170, yPos);
    
    // Notes
    if (quote.notes) {
      yPos += 15;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Notes:', 20, yPos);
      yPos += 5;
      const notesLines = doc.splitTextToSize(quote.notes, 170);
      doc.text(notesLines, 20, yPos);
    }
    
    // Pied de page
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(...grayColor);
    doc.text(COMPANY_INFO.name, 20, pageHeight - 20);
    doc.text(`${COMPANY_INFO.address}, ${COMPANY_INFO.postalCode} ${COMPANY_INFO.city}`, 20, pageHeight - 15);
    doc.text(`SIRET: ${COMPANY_INFO.siret}`, 20, pageHeight - 10);
    doc.text(`Tél: ${COMPANY_INFO.phone} | Email: ${COMPANY_INFO.email}`, 20, pageHeight - 5);
  
    // Télécharger le PDF
    const fileName = `Devis_${sanitizeFileName(quote.quote_number || quote.id || 'N/A')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Erreur lors de la génération du PDF du devis:', error);
    throw new Error(`Erreur lors de la génération du PDF: ${error.message}`);
  }
}

/**
 * Génère un PDF pour une facture
 */
export async function generateInvoicePDF(invoice, client, project) {
  try {
    if (!invoice) {
      throw new Error('Les données de la facture sont manquantes');
    }
    
    const doc = new jsPDF();
    
    // S'assurer que autoTable est disponible
    await ensureAutoTable(doc);
    
    // Couleurs
    const primaryColor = [22, 163, 74]; // Vert (primary-600)
    const grayColor = [107, 114, 128];
    
    // En-tête
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('FACTURE', 20, 25);
    
    // Informations entreprise
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(COMPANY_INFO.name, 20, 35);
    
    // Informations de la facture
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`N° Facture: ${invoice.invoice_number || 'N/A'}`, 150, 50);
    doc.text(`Date d'émission: ${formatDate(invoice.issue_date)}`, 150, 56);
    if (invoice.due_date) {
      doc.text(`Date d'échéance: ${formatDate(invoice.due_date)}`, 150, 62);
    }
    
    // Client
    const clientName = client 
      ? (client.client_type === 'entreprise' ? client.company_name : `${client.first_name || ''} ${client.last_name}`.trim())
      : 'Client non défini';
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Facturé à:', 20, 75);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(clientName, 20, 82);
    
    if (client?.address) {
      doc.text(client.address, 20, 88);
    }
    if (client?.phone) {
      doc.text(`Tél: ${client.phone}`, 20, 94);
    }
    if (client?.email) {
      doc.text(`Email: ${client.email}`, 20, 100);
    }
    if (client?.client_type === 'entreprise' && client?.siret) {
      doc.text(`SIRET: ${client.siret}`, 20, 106);
    }
    
    // Chantier (si applicable)
    let yPos = 110;
    if (project) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Chantier: ${project.name}`, 20, yPos);
      yPos += 8;
    }
    
    // Titre de la facture
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(invoice.title || 'Facture', 20, yPos);
    yPos += 10;
    
    // Description
    if (invoice.description) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const descriptionLines = doc.splitTextToSize(invoice.description, 170);
      doc.text(descriptionLines, 20, yPos);
      yPos += descriptionLines.length * 5 + 5;
    }
    
    // Tableau des montants
    yPos += 5;
    const tableData = [
      [
        invoice.title || 'Prestation',
        `${(invoice.total_amount || 0).toFixed(2)} €`,
        `${invoice.tax_rate || 20}%`,
        `${(invoice.tax_amount || 0).toFixed(2)} €`,
        `${(invoice.total_with_tax || 0).toFixed(2)} €`
      ]
    ];
    
    // Vérifier que autoTable est disponible
    if (typeof doc.autoTable === 'function') {
      doc.autoTable({
        startY: yPos,
        head: [['Description', 'Montant HT', 'Taux TVA', 'TVA', 'Montant TTC']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10 },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { halign: 'right' },
          2: { halign: 'center' },
          3: { halign: 'right' },
          4: { halign: 'right', fontStyle: 'bold' }
        }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    } else {
      // Fallback: créer un tableau simple sans autoTable
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Description', 20, yPos);
      doc.text('Montant HT', 80, yPos);
      doc.text('Taux TVA', 120, yPos);
      doc.text('TVA', 150, yPos);
      doc.text('Montant TTC', 170, yPos);
      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.text(invoice.title || 'Prestation', 20, yPos);
      doc.text(`${(invoice.total_amount || 0).toFixed(2)} €`, 80, yPos);
      doc.text(`${invoice.tax_rate || 20}%`, 120, yPos);
      doc.text(`${(invoice.tax_amount || 0).toFixed(2)} €`, 150, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(`${(invoice.total_with_tax || 0).toFixed(2)} €`, 170, yPos);
      yPos += 10;
    }
    
    // Total
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Total TTC:', 150, yPos);
    doc.text(`${(invoice.total_with_tax || 0).toFixed(2)} €`, 170, yPos);
    
    // Paiement
    const paidAmount = parseFloat(invoice.paid_amount) || 0;
    if (paidAmount > 0) {
      yPos += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Montant payé: ${paidAmount.toFixed(2)} €`, 150, yPos);
      const remaining = (invoice.total_with_tax || 0) - paidAmount;
      if (remaining > 0) {
        yPos += 6;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(220, 38, 38); // Rouge
        doc.text(`Reste à payer: ${remaining.toFixed(2)} €`, 150, yPos);
        doc.setTextColor(0, 0, 0);
      } else {
        yPos += 6;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(22, 163, 74); // Vert
        doc.text('Payé intégralement', 150, yPos);
        doc.setTextColor(0, 0, 0);
      }
    }
    
    if (invoice.payment_method) {
      yPos += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Mode de paiement: ${invoice.payment_method}`, 20, yPos);
      if (invoice.payment_reference) {
        doc.text(`Référence: ${invoice.payment_reference}`, 20, yPos + 6);
      }
    }
    
    // Notes
    if (invoice.notes) {
      yPos += 15;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Notes:', 20, yPos);
      yPos += 5;
      const notesLines = doc.splitTextToSize(invoice.notes, 170);
      doc.text(notesLines, 20, yPos);
    }
    
    // Pied de page
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(...grayColor);
    doc.text(COMPANY_INFO.name, 20, pageHeight - 20);
    doc.text(`${COMPANY_INFO.address}, ${COMPANY_INFO.postalCode} ${COMPANY_INFO.city}`, 20, pageHeight - 15);
    doc.text(`SIRET: ${COMPANY_INFO.siret}`, 20, pageHeight - 10);
    if (COMPANY_INFO.tva) {
      doc.text(`TVA: ${COMPANY_INFO.tva}`, 20, pageHeight - 5);
    }
    doc.text(`Tél: ${COMPANY_INFO.phone} | Email: ${COMPANY_INFO.email}`, 150, pageHeight - 10);
  
    // Télécharger le PDF
    const fileName = `Facture_${sanitizeFileName(invoice.invoice_number || invoice.id || 'N/A')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Erreur lors de la génération du PDF de la facture:', error);
    throw new Error(`Erreur lors de la génération du PDF: ${error.message}`);
  }
}

